import { Trans } from '@lingui/react/macro';
import { Clock, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Item, ItemActions, ItemContent, ItemDescription, ItemHeader, ItemTitle } from '@/components/ui/item';
import type { CollisionInfo } from '@/types/api';
import { cn } from '@/utils/cn';
import { formatDuration } from '@/utils/time';

interface CollisionCardProps {
  collision: CollisionInfo;
  fileName: string;
  sequentialNumber: number;
  onJumpToTime?: (seconds: number) => void;
  currentVideoTime?: number;
}

const isHighlighted = (collision: CollisionInfo, currentVideoTime?: number): boolean => {
  if (currentVideoTime === undefined) return false;
  return collision.video_start_seconds <= currentVideoTime && currentVideoTime <= collision.video_end_seconds;
};

export const CollisionCard = ({
  collision,
  fileName,
  sequentialNumber,
  onJumpToTime,
  currentVideoTime,
}: CollisionCardProps): React.ReactElement => {
  const highlighted = isHighlighted(collision, currentVideoTime);

  return (
    <Item
      variant="outline"
      className={cn('my-2 cursor-pointer hover:shadow-lg', highlighted && 'ring ring-3 ring-red-600')}
      render={<button type="button" />}
      onClick={() => onJumpToTime?.(collision.video_start_seconds)}
    >
      <ItemHeader>
        <ItemTitle>
          <span className="font-semibold text-red-600">#{sequentialNumber}</span>
          {collision.drill_side}
        </ItemTitle>
        <ItemActions>
          <Badge variant="outline" className="">
            <Trans>{collision.depth.toFixed(1)} mm</Trans>
          </Badge>
        </ItemActions>
      </ItemHeader>

      <ItemContent>
        <ItemDescription className="text-slate-500">
          {collision.line_number !== null && (
            <div className="flex items-center gap-1" title={collision.operation}>
              <FileText className="h-3 w-3" />
              <div>
                <Trans>
                  <span className="truncate font-medium">{fileName}</span>:{' '}
                  <span className="text-slate-400">line {collision.line_number}</span>
                </Trans>
              </div>
            </div>
          )}
        </ItemDescription>
        <ItemDescription className="text-slate-500">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <div>
              {formatDuration(collision.video_start_seconds)} – {formatDuration(collision.video_end_seconds)}
            </div>
          </div>
        </ItemDescription>
      </ItemContent>
    </Item>
  );
};
